<template>
  <div :class="['c-search-cards', previewClass]">
    <div class="c-search-cards__heading">
      <h3 class="c-search-cards__title">
        {{ title }}
      </h3>

      <a
        v-if="!isFetching"
        class="c-search-cards__button o-button -md-secondary -md-small -mobile-only"
        :href="action.url"
      >
        {{ action.label }}
      </a>
    </div>

    <div class="c-search-cards__items">
      <template v-if="isFetching">
        <div
          v-for="index in cardsLimit"
          :key="`placeholder-card-${index}`"
          class="c-search-cards__item"
        >
          <div
            :class="`c-search-cards__avatar-image is-fetching -${cardRadius}`"
          />

          <span class="c-search-cards__label is-fetching" />
        </div>
      </template>

      <template v-else>
        <div
          v-for="(card, index) in slicedCards"
          :key="`card-${index}`"
          class="c-search-cards__item"
        >
          <a
            v-if="card.image_public_id"
            :class="`c-search-cards__avatar -${cardRadius}`"
            :href="card.url"
          >
            <img
              :class="`c-search-cards__avatar-image -${cardRadius}`"
              :alt="`Imagem do avatar de ${card.label}`"
              :src="
                card.image_public_id
                  | enjuphotosUrl(enjuphotos.presets.userStore.avatar)
              "
            />
          </a>

          <a class="c-search-cards__label" :href="card.url">
            {{ card.label }}
          </a>
        </div>
      </template>
    </div>

    <a
      v-if="!isFetching"
      class="c-search-cards__button o-button -xs-secondary -xs-small -desktop-only"
      :href="action.url"
    >
      {{ action.label }}
    </a>
  </div>
</template>

<script>
  import '@/tools/filters/enjuphotos-url'

  import media from '@/tools/mixins/media'
  import { ENJUPHOTOS_PRESETS } from '@/tools/enjuphotos'

  export default {
    mixins: [media],
    props: {
      title: {
        type: String,
        required: true
      },
      action: {
        type: Object,
        default: () => {}
      },
      cards: {
        type: Array,
        default: () => []
      },
      cardRadius: {
        type: String,
        default: 'circle'
      },
      isFetching: {
        type: Boolean,
        default: false
      },
      previewClass: {
        type: String,
        default: '',
        validator: (value) =>
          ['', '-mobile-preview', '-desktop-preview'].indexOf(value) !== -1
      }
    },
    data() {
      return {
        cardsLimit: 4,
        enjuphotos: {
          presets: ENJUPHOTOS_PRESETS
        }
      }
    },
    computed: {
      slicedCards() {
        return this.cards.slice(0, this.cardsLimit)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @mixin titleDesktopStyles() {
    margin-bottom: 24px;
    margin-left: 0;
  }

  @mixin itemsMobileStyles() {
    flex-direction: row;
    padding: 0 20px;
  }

  @mixin itemMobileStyles() {
    flex-basis: 25%;
    max-width: 100px;
    flex-direction: column;
    row-gap: 12px;
    text-align: center;
    padding-right: 24px;

  }

  @mixin avatarMobileStyles() {
    height: 76px;
    width: 76px;
    margin-bottom: 12px;

    &.-square {
      @include breakpoint($until: md) {
        height: 104px;
        width: 104px;
        margin-bottom: 0;
      }
    }
  }

  @mixin labelMobileStyles() {
    max-width: 104px;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  @mixin buttonMobileStyles() {
    line-height: 24px;
    margin-right: 24px;
    margin-top: 0;

    &.-desktop-only {
      display: none;
    }

    &.-mobile-only {
      display: block;
      font-size: 16px;
      font-weight: $font-weight-bold;
      margin-top: 4px;
    }
  }

  .c-search-cards__title {
    color: $color-neutral-900;
    font-size: 20px;
    font-weight: $font-weight-semi-bold;
    line-height: 28px;
    margin-bottom: 24px;
    margin-left: 20px;

    @include breakpoint($until: md) {
      margin-bottom: 16px;
    }
  }

  .c-search-cards__items {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include breakpoint($until: md) {
      overflow: auto;

      &::-webkit-scrollbar {
        -webkit-appearance: none !important;
      }
    }
  }

  .c-search-cards__item {
    align-items: center;
    column-gap: 16px;
    display: flex;
  }

  .c-search-cards__avatar,
  .c-search-cards__avatar-image {
    height: 76px;
    width: 76px;

    &.-circle {
      border-radius: 500px;

      @include breakpoint($until: md) {
        height: 76px;
        width: 76px;
      }
    }

    &.-square {
      border-radius: 8px;
    }
  }

  .c-search-cards__avatar-image {
    &.is-fetching {
      @include content-placeholder;

      border: none;
    }
  }

  .c-search-cards__label {
    color: $color-neutral-900;
    font-size: 14px;
    font-weight: $font-weight-medium;
    line-height: 18px;
    letter-spacing: .16px;
    text-transform: none;

    &.is-fetching {
      @include content-placeholder;

      width: 78px;
      height: 20px;
    }
  }

  .c-search-cards__button {
    margin-top: 28px;

    &.-desktop-only {
      display: inline-block;
    }

    &.-mobile-only {
      display: none;
    }
  }

  .c-search-cards {
    &:not(.-desktop-preview) {
      @include breakpoint($until: lg) {
        .c-search-cards__items {
          @include itemsMobileStyles;
        }

        .c-search-cards__item {
          @include itemMobileStyles;
        }

        .c-search-cards__avatar,
        .c-search-cards__avatar-image {
          @include avatarMobileStyles;
        }

        .c-search-cards__label {
          @include labelMobileStyles;
        }

        .c-search-cards__button {
          @include buttonMobileStyles;
        }
      }

      @include breakpoint($until: sm) {
        .c-search-cards__item {
          flex: 1 0 0;
          padding-right: 0;
        }
      }
    }

    &:not(.-mobile-preview) {
      .c-search-cards__title {
        @include breakpoint(lg) {
          @include titleDesktopStyles;
        }
      }
    }

    &.-desktop-preview{
      .c-search-cards__title {
        @include titleDesktopStyles;
      }
    }

    &.-mobile-preview {
      .c-search-cards__items {
        @include itemsMobileStyles;
      }

      .c-search-cards__item {
        @include itemMobileStyles;
      }

      .c-search-cards__avatar,
      .c-search-cards__avatar-image {
        @include avatarMobileStyles;
      }

      .c-search-cards__label {
        @include labelMobileStyles;
      }

      .c-search-cards__button {
        @include buttonMobileStyles;
      }
    }
  }

  .c-search-cards__heading {
    display: flex;
    justify-content: space-between;
  }
</style>
